import { query as originQuery } from '../metricsData/schema';
import humanizeValue from '@utils/overview/humanizeValue';
import { humanize } from '@utils';
import { metricKeys } from '@pulse/metrics';
import { get, isEmpty } from 'lodash';
import { MessageConfig } from './reducer';
import moment from 'moment';
import { TOOLTIP_LABEL_FORMAT } from '@components/common/Charts/utils';

const ImpactedDevicesKey = 'Impacted Devices';
const NonImpactedDevicesKey = 'Non-Impacted Devices';
const ImpactedViewersKey = 'Impacted Viewers';
const NonImpactedViewersKey = 'Non-Impacted Viewers';

const Messages = {
  [ImpactedDevicesKey]: 'Devices with impacted streams',
  [NonImpactedDevicesKey]: 'Devices with no impacted streams',
  [ImpactedViewersKey]: 'Viewers with impacted streams',
  [NonImpactedViewersKey]: 'Viewers with no impacted streams',
};

const tooltipFormatter = (series) => {
  try {
    const { value } = series[0].data;
    const timestamp = moment(value[0]).format(TOOLTIP_LABEL_FORMAT);

    const rows = series.map((item) => {
      const value = humanize(item.data.value[1], '');
      const displayName = item.seriesName;
      return `<br/>${displayName} - <b>${value}</b>`;
    });
    return `${timestamp} ${rows.reverse()}`;
  } catch (err) {
    return '-';
  }
};

const createQuery = ({ interval, filter, tabKey, kpiConfig }) => {
  const { metrics, granularity } = MessageConfig[tabKey];
  const query = {
    ...originQuery,
    interval,
    filter,
    granularity,
    metrics,
  };
  query.options.kpiConfig = kpiConfig;
  return query;
};

const formatStreamsData = (data) => {
  try {
    const formattedResponse = {} as any;
    if (!data.isFetching && data.response != null) {
      const totals = data.response.totals;
      // if there is an empty response from backend, condider 0 (for 'ALL' granularity)
      if (!isEmpty(totals)) {
        for (const [key, value] of Object.entries(totals)) {
          const response = humanizeValue(value as number, key);
          formattedResponse[key] = `${response.humanizedNumber} ${response.humanizedUnit}`;
        }
        // only show playing time metrics if good playing time is 10% higher than bad
        // also store raw value for progress bar
        formattedResponse.showPlayingTime =
          totals.GoodSessionAverageLifePlayingTimeMins >
          totals.BadSessionAverageLifePlayingTimeMins * 1.1;
        formattedResponse.rawBadSessionPct = totals.BadSessionPct;

        return formattedResponse;
      }
    }
  } catch (err) {
    // shows 0 values
  }

  // no data scenario
  return {
    [metricKeys.BadSession]: '0',
    [metricKeys.BadSessionPct]: '0',
    [metricKeys.GoodSessionAverageLifePlayingTimeMins]: '0',
    [metricKeys.BadSessionAverageLifePlayingTimeMins]: '0',
    showPlayingTime: false,
  };
};

const buildChartOptions = ({ nonImpactedKey, impactedKey, goodBarData, badBarData }) => {
  const option = {
    grid: {
      top: 20,
      left: 40,
      right: 20,
      bottom: 5,
    },
    legend: {
      right: 20,
      itemWidth: 15,
      itemHeight: 12,
      itemStyle: {
        borderColor: '#aaa',
      },
      textStyle: {
        fontSize: 12,
      },
      tooltip: {
        show: true,
        formatter: (item) => Messages[item.name],
      },
    },
    xAxis: {
      type: 'time',
      show: false,
    },
    yAxis: {
      type: 'value',
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        formatter: (value) => humanize(value, ''),
        color: '#333',
        fontSize: 12,
        showMinLabel: false,
      },
      splitNumber: 2,
      splitLine: {
        lineStyle: {
          color: '#e5e5e5a3',
        },
      },
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
      formatter: tooltipFormatter,
    },
    series: [
      {
        name: nonImpactedKey,
        type: 'bar',
        stack: 'devices',
        data: goodBarData,
        itemStyle: {
          color: '#E0F4EA',
          borderColor: '#00A150',
        },
        cursor: 'default',
      },
      {
        name: impactedKey,
        type: 'bar',
        stack: 'devices',
        data: badBarData,
        itemStyle: {
          color: '#F3D4D4',
          borderColor: '#C32828',
        },
        cursor: 'default',
      },
    ],
  };
  return option;
};

const formatDevicesData = (data) => {
  const goodBarData: any = [];
  const badBarData: any = [];

  const records = get(data, 'response.data', []);
  records.forEach(({ timestamp, results }) => {
    goodBarData.push({ value: [timestamp, results.GoodUniqueDevices] });
    badBarData.push({ value: [timestamp, results.BadUniqueDevices] });
  });

  const options = buildChartOptions({
    impactedKey: ImpactedDevicesKey,
    nonImpactedKey: NonImpactedDevicesKey,
    goodBarData,
    badBarData,
  });

  return options;
};

const formatViewersData = (data, selectedBarIndex) => {
  const goodBarData: any = [];
  const badBarData: any = [];

  const defItemStyle = { opacity: 1 };

  const records = get(data, 'response.data', []);
  records.forEach(({ timestamp, results }) => {
    goodBarData.push({
      value: [timestamp, results.GoodUniqueViewers],
      itemStyle: defItemStyle,
    });
    badBarData.push({
      value: [timestamp, results.BadUniqueViewers],
      itemStyle: defItemStyle,
    });
  });

  // highlight a section
  if (records.length && selectedBarIndex !== null) {
    const highlightIndex = selectedBarIndex === -1 ? goodBarData.length - 1 : selectedBarIndex;
    if (goodBarData.length >= highlightIndex) {
      goodBarData[highlightIndex].itemStyle = {
        color: '#ADE0C1',
        opacity: 1,
      };
      badBarData[highlightIndex].itemStyle = {
        opacity: 1,
        color: '#EFABAB',
      };

      // update all referenced bars
      defItemStyle.opacity = 0.4;
    }
  }

  const options = buildChartOptions({
    impactedKey: ImpactedViewersKey,
    nonImpactedKey: NonImpactedViewersKey,
    goodBarData,
    badBarData,
  });

  return options;
};

export { createQuery, formatStreamsData, formatDevicesData, formatViewersData };
